import UniversalAdsConnectCard from './UniversalAdsConnectCard';
import SourcesLoading, { SourcesProgress } from './SourcesLoading';
import MetaCampaignEditor from './MetaCampaignEditor';
import WatchedCampaignActions from './WatchedCampaignActions';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  activateConnection,
  campaignErrorMessage,
  disconnectConnection,
  getConnection,
  getAnalysisQuota,
  importCampaigns,
  listAdAccounts,
  listFacebookCampaigns,
  listImportedCampaigns,
  prepareConnection,
  refreshMetaSnapshot,
  runDailyNow,
  stopWatchingCampaign,
} from '../api/adpilotApi';
import { demoAdAccounts, demoCampaigns, demoConnection } from '../demoData';
import GoogleAdsConnectCard from './GoogleAdsConnectCard';
import MetaAccessRequest from './MetaAccessRequest';
import ChatGptAdsConnectCard from './ChatGptAdsConnectCard';
import ChannelTabs, { readActiveChannel } from './ChannelTabs';

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const CAMPAIGN_LIMIT = 20;

function tokenIsUsable(token) {
  if (!token) return false;
  if (!token.expires_at) return true;
  return new Date(token.expires_at).getTime() > Date.now();
}

function ConnectPage({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const briefPath = location.pathname.startsWith('/demo') ? '/demo/recommendations' : '/recommendations';
  const oauthState = location.state || {};
  // The OAuth callback bounces back here carrying its provider. That consent
  // just happened on THAT channel, so land on its tab — the account picker it
  // unlocked must not hide behind whichever tab was stored. Otherwise the
  // selection follows the user from Brief/Activity/Create.
  const [activeChannel, setActiveChannel] = useState(() => (
    oauthState.oauth_provider === 'google-ads' ? 'google-ads'
      : (oauthState.oauth_provider ? 'facebook' : readActiveChannel())
  ));
  const isMetaTab = activeChannel === 'facebook';
  const [connection, setConnection] = useState(user.preview ? demoConnection : null);
  const [authorizationUrl, setAuthorizationUrl] = useState(null);
  const [previouslyAuthorized, setPreviouslyAuthorized] = useState(false);
  const [connectionLoaded, setConnectionLoaded] = useState(Boolean(user.preview));
  const [adAccounts, setAdAccounts] = useState(user.preview ? demoAdAccounts : []);
  const [fbCampaigns, setFbCampaigns] = useState(user.preview ? demoCampaigns : []);
  const [imported, setImported] = useState(user.preview ? demoCampaigns.slice(0, 2) : []);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [showAccountPicker, setShowAccountPicker] = useState(false);
  const [analysisQuota, setAnalysisQuota] = useState(user.preview ? { limit: 3, remaining: 3 } : null);
  const [metaSync, setMetaSync] = useState(null);
  const [editCampaign, setEditCampaign] = useState(null);
  const pollRef = useRef(null);
  const busyRef = useRef(false);
  const importedSeq = useRef(0);
  const userId = user.id;

  const refreshImported = async (activeConnection) => {
    if (!activeConnection?.connection_id) return;
    const seq = ++importedSeq.current;
    // scope 'all': imported ⊇ watched. Each row carries `watched`; the slot
    // math below runs on the watched subset, never on the import count.
    const result = await listImportedCampaigns({
      userId, connectionId: activeConnection.connection_id, scope: 'all', includeSync: true,
    });
    if (seq === importedSeq.current) {
      setImported(Array.isArray(result) ? result : result.campaigns);
      setMetaSync(result.sync || null);
    }
  };

  const refreshFacebookCampaigns = async (activeConnection) => {
    if (!activeConnection?.ad_account_id) return;
    setFbCampaigns(await listFacebookCampaigns({
      userId, connectionId: activeConnection.connection_id,
    }));
  };

  const refreshAdAccounts = async (activeConnection) => {
    if (!activeConnection?.connection_id) return;
    setAdAccounts(await listAdAccounts({
      userId, connectionId: activeConnection.connection_id,
    }));
  };

  const refreshQuota = async (activeConnection) => {
    if (!activeConnection?.connection_id) return;
    try {
      setAnalysisQuota(await getAnalysisQuota({ userId, connectionId: activeConnection.connection_id }));
    } catch {
      setAnalysisQuota(null);
    }
  };

  useEffect(() => {
    if (user.preview) return undefined;
    let cancelled = false;
    setConnectionLoaded(false);
    const load = async () => {
      try {
        if (oauthState.oauth_error && oauthState.oauth_provider !== 'google-ads') {
          setError(oauthState.oauth_error_description || oauthState.oauth_error);
        }
        let current;
        try {
          current = (await getConnection({ userId })).connection;
        } catch (connError) {
          if (connError?.response?.status !== 404) throw connError;
          current = null;
        }
        if (cancelled) return;

        setPreviouslyAuthorized(Boolean(current?.token || current?.ad_account_id));

        if (current?.access_role && current.access_role !== 'owner') {
          setConnection(current);
          if (current.ad_account_id) {
            await Promise.all([
              refreshFacebookCampaigns(current),
              refreshImported(current),
              refreshQuota(current),
            ]);
          }
          return;
        }

        if (!current || !tokenIsUsable(current.token)) {
          if (current) setConnection(current);
          const prepared = await prepareConnection({ userId });
          if (cancelled) return;
          setAuthorizationUrl(prepared.authorization_url);
          setConnection(prepared.connection || current);
          return;
        }

        setConnection(current);
        await refreshAdAccounts(current);
        if (cancelled) return;
        if (current.ad_account_id) {
          await Promise.all([refreshFacebookCampaigns(current), refreshImported(current), refreshQuota(current)]);
        }
      } catch (loadError) {
        if (!cancelled) setError(campaignErrorMessage(loadError, 'Failed to load your Meta connection'));
      } finally {
        if (!cancelled) setConnectionLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user.preview]);

  useEffect(() => {
    if (user.preview) return undefined;
    if (!connection || tokenIsUsable(connection.token) || !connection.connection_id) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const current = (await getConnection({ userId })).connection;
        if (current && tokenIsUsable(current.token)) {
          clearInterval(pollRef.current);
          setConnectionLoaded(false);
          setConnection(current);
          try {
            await refreshAdAccounts(current);
            if (current.ad_account_id) {
              await Promise.all([refreshFacebookCampaigns(current), refreshImported(current), refreshQuota(current)]);
            }
          } catch (loadError) {
            setError(campaignErrorMessage(loadError, 'Failed to load your Meta sources'));
          } finally { setConnectionLoaded(true); }
        } else setConnection(current);
      } catch {
        // OAuth may still be in flight.
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connection?.token, connection?.connection_id, userId, user.preview]);

  const handleSelectAccount = async (adAccountId) => {
    if (busyRef.current) return;
    const switching = Boolean(connection?.ad_account_id && connection.ad_account_id !== adAccountId);
    if (switching && !window.confirm('Switch ad accounts? Each account keeps its own watched campaigns and history.')) return;
    if (user.preview) {
      setConnection((prev) => ({ ...prev, ad_account_id: adAccountId }));
      setShowAccountPicker(false);
      setMessage('Ad account switched. This account’s watched campaigns are ready to manage.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setConnectionLoaded(false);
    setMetaSync(null);
    setEditCampaign(null);
    setError(null);
    try {
      const updated = (await activateConnection({
        userId, connectionId: connection.connection_id, adAccountId,
      })).connection;
      const activeConnection = { ...connection, ...updated };
      setConnection(activeConnection);
      await Promise.all([refreshFacebookCampaigns(activeConnection), refreshImported(activeConnection), refreshQuota(activeConnection)]);
      setSelectedIds(new Set());
      setShowAccountPicker(false);
      setMessage(switching
        ? 'Ad account switched. This account’s watched campaigns are ready to manage.'
        : 'Meta account connected. Choose the campaigns you want AdPilot to watch.');
    } catch (applyError) {
      setError(campaignErrorMessage(applyError, 'Could not select this ad account'));
    } finally {
      busyRef.current = false;
      setBusy(false);
      setConnectionLoaded(true);
    }
  };

  const handleRefreshMeta = async () => {
    if (busyRef.current || user.preview) return;
    busyRef.current = true; setBusy(true); setError(null);
    try {
      const snapshot = await refreshMetaSnapshot({ userId, connectionId: connection.connection_id });
      setMetaSync({ refreshed_at: snapshot.refreshed_at, stale: snapshot.stale, error: snapshot.sync_error });
      if (snapshot.stale) setImported((rows) => rows.map((row) => ({ ...row, stale: true })));
      if (!snapshot.stale) await Promise.all([refreshImported(connection), refreshFacebookCampaigns(connection)]);
    } catch (refreshError) {
      setMetaSync((current) => current ? { ...current, stale: true } : current);
      setImported((rows) => rows.map((row) => ({ ...row, stale: true })));
      setError(campaignErrorMessage(refreshError, 'Could not refresh Meta. The displayed values may be out of date.'));
    } finally { busyRef.current = false; setBusy(false); }
  };

  // Watched subset of the imported catalog. Missing `watched` (preview seed
  // data, pre-split responses) counts as watched — those rows only ever held
  // the working set.
  const watchedList = imported.filter((campaign) => campaign.watched !== false);

  const toggleCampaign = (platformCampaignId) => {
    if (watchedList.some((campaign) => String(campaign.platform_campaign_id) === String(platformCampaignId))) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(platformCampaignId)) next.delete(platformCampaignId);
      else if (next.size < Math.max(0, CAMPAIGN_LIMIT - watchedList.length)) next.add(platformCampaignId);
      return next;
    });
  };

  const handleImport = async () => {
    if (busyRef.current) return;
    const campaignIds = [...selectedIds];
    if (campaignIds.length === 0) {
      setError('Choose at least one campaign.');
      return;
    }
    if (user.preview) {
      setImported((current) => {
        const have = new Set(current.map((campaign) => String(campaign.platform_campaign_id)));
        const slots = Math.max(0, CAMPAIGN_LIMIT - current.filter((campaign) => campaign.watched !== false).length);
        const added = fbCampaigns
          .filter((campaign) => selectedIds.has(campaign.platform_campaign_id) && !have.has(String(campaign.platform_campaign_id)))
          .slice(0, slots)
          .map((campaign) => ({ ...campaign, watched: true }));
        return [...current, ...added];
      });
      setSelectedIds(new Set());
      setShowImporter(false);
      navigate(briefPath);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await importCampaigns({
        userId, connectionId: connection.connection_id, campaignIds,
      });
      setSelectedIds(new Set());
      setShowImporter(false);
      await refreshImported(connection);

      // Import and first analysis are one product action. Approval remains a
      // separate trust boundary; this dry run cannot change Meta.
      let analyzing = false;
      if ((analysisQuota?.remaining ?? 1) > 0) {
        try {
          const stats = await runDailyNow({ userId, connectionId: connection.connection_id });
          setAnalysisQuota({ limit: stats.limit, remaining: stats.remaining, retry_at: stats.retry_at });
          analyzing = Boolean(stats.dispatched);
        } catch (runError) {
          setError(`Campaigns are connected, but analysis did not start: ${campaignErrorMessage(runError)}`);
          return;
        }
      }
      navigate(analyzing ? `${briefPath}?analyzing=1` : briefPath, { replace: true });
    } catch (importError) {
      setError(campaignErrorMessage(importError, 'Could not finish campaign setup'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // Import every campaign on the ad account WITHOUT watching any — monitoring
  // stays a deliberate, capped choice. The import is what makes the whole
  // catalog available to watch toggles below.
  const handleImportAll = async () => {
    if (busyRef.current) return;
    if (user.preview) {
      setImported((current) => {
        const have = new Set(current.map((campaign) => String(campaign.platform_campaign_id)));
        return [
          ...current,
          ...fbCampaigns.filter((campaign) => !have.has(String(campaign.platform_campaign_id)))
            .map((campaign) => ({ ...campaign, watched: false })),
        ];
      });
      setMessage(`All campaigns imported. Watching is still limited to ${CAMPAIGN_LIMIT} — choose which below.`);
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await importCampaigns({ userId, connectionId: connection.connection_id, all: true });
      await refreshImported(connection);
      setMessage(`Imported ${result.synced} campaign${result.synced === 1 ? '' : 's'}. Watching is still limited to ${CAMPAIGN_LIMIT} — choose which below.`);
    } catch (importError) {
      setError(campaignErrorMessage(importError, 'Could not import campaigns'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // Promote an already-imported campaign into a free monitoring slot. This is
  // a default-mode import of one id: the server re-syncs its metadata from the
  // Graph and force-sets is_watched, enforcing the slot cap atomically.
  const handleWatch = async (platformCampaignId) => {
    if (busyRef.current) return;
    if (user.preview) {
      setImported((current) => current.map((campaign) => (
        String(campaign.platform_campaign_id) === String(platformCampaignId)
          ? { ...campaign, watched: true }
          : campaign
      )));
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await importCampaigns({ userId, connectionId: connection.connection_id, campaignIds: [platformCampaignId] });
      await refreshImported(connection);
    } catch (watchError) {
      setError(campaignErrorMessage(watchError, 'Could not watch this campaign'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleStopWatching = async (platformCampaignId) => {
    if (busyRef.current) return;
    if (user.preview) {
      setImported((current) => current.map((campaign) => (
        String(campaign.platform_campaign_id) === String(platformCampaignId)
          ? { ...campaign, watched: false }
          : campaign
      )));
      setMessage('Campaign removed from the active working set. Its history is preserved.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await stopWatchingCampaign({ userId, connectionId: connection.connection_id, platformCampaignId });
      await refreshImported(connection);
      setMessage('Campaign removed from the active working set. Its history is preserved.');
    } catch (removeError) {
      setError(campaignErrorMessage(removeError, 'Could not stop watching this campaign'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (busyRef.current) return;
    if (!window.confirm(
      'Disconnect Meta from AdPilot?\n\n'
      + 'Syncing and analysis stop right away, and we revoke the authorization to read this account. '
      + 'Your imported campaigns, past briefs, and history are kept — reconnect any time and pick up where you left off.',
    )) return;
    if (user.preview) {
      setConnection(null);
      setAdAccounts([]);
      setFbCampaigns([]);
      setImported([]);
      setMessage('Meta disconnected.');
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await disconnectConnection({ userId, connectionId: connection?.connection_id, provider: 'facebook' });
      setConnection(null);
      setAdAccounts([]);
      setFbCampaigns([]);
      setImported([]);
      setSelectedIds(new Set());
      setShowAccountPicker(false);
      setShowImporter(false);
      setMessage('Meta disconnected. Your campaign history is kept — reconnect any time.');
    } catch (disconnectError) {
      setError(campaignErrorMessage(disconnectError, 'Could not disconnect Meta'));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const owner = !connection?.access_role || connection.access_role === 'owner';
  const connected = Boolean(connection && (tokenIsUsable(connection.token) || !owner));
  const active = Boolean(connection?.ad_account_id);
  const selectedAccount = adAccounts.find((account) => account.ad_account_id === connection?.ad_account_id);
  // Slots are a MONITORING resource; importing consumes none of them.
  const availableCampaignSlots = Math.max(0, CAMPAIGN_LIMIT - watchedList.length);
  const unwatchedImported = imported.filter((campaign) => campaign.watched === false);
  // Campaigns on the ad account that were never imported — what "Import all"
  // still has to fetch. Watching via the picker imports on the way, so this
  // only counts genuinely unknown campaigns.
  const unimportedCount = fbCampaigns
    .filter((campaign) => !imported.some((item) => String(item.platform_campaign_id) === String(campaign.platform_campaign_id)))
    .length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Sources</p>
          <h1 className="page-title">Connect your campaigns.</h1>
          <p className="page-subtitle">AdPilot reads performance and prepares changes. Nothing is published without your approval.</p>
        </div>
      </header>

      <ChannelTabs active={activeChannel} onSelect={setActiveChannel} />

      {isMetaTab && (!connectionLoaded ? <SourcesLoading platform="Meta" /> : (
        <>
      {busy && <SourcesProgress platform="Meta" updating />}
      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {message && <div className="notice" role="status">{message}</div>}

      <div className="connection-summary" aria-label="Connection status">
        <span className="summary-pill">{connected ? 'Meta connected' : 'Meta not connected'}</span>
        {active && <span className="summary-pill">{selectedAccount?.name || connection.ad_account_id}</span>}
        {watchedList.length > 0 && <span className="summary-pill">{watchedList.length} campaign{watchedList.length === 1 ? '' : 's'} watched</span>}
        {unwatchedImported.length > 0 && <span className="summary-pill">{imported.length} imported</span>}
        {active && owner && <button type="button" className="summary-action" disabled={busy} onClick={() => setShowAccountPicker((value) => !value)}>Switch ad account</button>}
        {connected && owner && <button type="button" className="summary-action" disabled={busy} onClick={handleDisconnect}>Disconnect</button>}
      </div>

      {active && !user.preview && <div className="meta-sync-summary">
        <span>{metaSync?.refreshed_at ? `Last synced ${new Date(metaSync.refreshed_at).toLocaleString()}` : 'Meta sync time unavailable'}</span>
        <button type="button" className="text-button" disabled={busy} onClick={handleRefreshMeta}>Refresh from Meta</button>
        <small>Watching controls AdPilot analysis. Active or paused controls delivery in Meta.</small>
      {metaSync?.stale && <p role="status">{metaSync.error || 'These values may be out of date. Refresh Meta before making changes.'}</p>}
      </div>}

      {!connected && (
        <section className="card">
          <p className="eyebrow">Meta Ads</p>
          {previouslyAuthorized ? (
            <>
              <h2 className="card-title">Reconnect your Meta account</h2>
              <p className="card-copy">Sign in to Meta again to renew your connection. Your selected ad account, watched campaigns, and history are saved. If your app access is still in place, you don’t need another access request or invitation.</p>
              <div className="campaign-actions" style={{ marginTop: 18 }}>
                {authorizationUrl
                  ? <a className="btn btn-primary" href={authorizationUrl}>Reconnect Meta</a>
                  : <button type="button" className="btn btn-primary" disabled>Preparing connection…</button>}
              </div>
              <details className="card-copy meta-access-help"><summary>Need help getting app access?</summary><MetaAccessRequest user={user} authorizationUrl={authorizationUrl} /></details>
            </>
          ) : !user.preview && <MetaAccessRequest user={user} authorizationUrl={authorizationUrl} />}
        </section>
      )}

      {connected && (!active || showAccountPicker) && (
        <section className="card">
          <p className="eyebrow">Ad account</p>
          <h2 className="card-title">{active ? 'Switch Meta ad account' : 'Which account should AdPilot watch?'}</h2>
          <p className="card-copy">{active
            ? 'Each ad account keeps its own watched campaigns, recommendations, and Activity history. Switch back anytime.'
            : 'Choose one now. You can return to Sources whenever you need to update it.'}</p>
          <div className="account-list">
            {adAccounts.length === 0 && !error && <p className="card-copy">No ad accounts are available to this Meta login.</p>}
            {adAccounts.map((account) => (
              <button
                key={account.ad_account_id}
                type="button"
                className="account-button"
                disabled={busy}
                onClick={() => handleSelectAccount(account.ad_account_id)}
              >
                <span><strong>{account.name || 'Meta ad account'}</strong><small>{account.currency} · {account.account_status === 1 ? 'Active' : `Status ${account.account_status}`}</small></span>
                <span>{account.ad_account_id === connection?.ad_account_id ? 'Current' : 'Choose →'}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {active && !showAccountPicker && (
        <>
          <section className="card">
            <div className="section-heading" style={{ marginTop: 0 }}>
              <div>
                <h2>{watchedList.length ? 'Campaigns AdPilot watches' : 'Choose your campaigns'}</h2>
                <span>{watchedList.length ? `${watchedList.length} of ${CAMPAIGN_LIMIT} monitoring slots used.` : `Watch up to ${CAMPAIGN_LIMIT}. Setup and the first analysis happen together.`}</span>
              </div>
              {watchedList.length > 0 && (
                <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setShowImporter((value) => !value)}>
                  {showImporter ? 'Done' : 'Manage monitoring'}
                </button>
              )}
            </div>

            {(watchedList.length === 0 || showImporter) && (
              <>
                {fbCampaigns.length === 0 && !error && <p className="card-copy">No campaigns were returned by Meta.</p>}
                <div className="campaign-grid">
                  {fbCampaigns.map((campaign) => {
                    const watched = watchedList.some((item) => String(item.platform_campaign_id) === String(campaign.platform_campaign_id));
                    return (
                      <label key={campaign.platform_campaign_id} className="campaign-option">
                        <input
                          type="checkbox"
                          aria-label={`${watched ? 'Stop watching' : 'Select'} ${campaign.name}`}
                          disabled={busy || (!watched && !selectedIds.has(campaign.platform_campaign_id) && selectedIds.size >= availableCampaignSlots)}
                          checked={selectedIds.has(campaign.platform_campaign_id) || watched}
                          onChange={() => (watched ? handleStopWatching(campaign.platform_campaign_id) : toggleCampaign(campaign.platform_campaign_id))}
                        />
                        <span className="campaign-option-copy">
                          <strong title={campaign.name}>{campaign.name}</strong>
                          <small>{campaign.status} · {campaign.daily_budget ? `${dollars.format(campaign.daily_budget / 100)}/day` : 'No campaign budget'}</small>
                        </span>
                      </label>
                    );
                  })}
                </div>
                {fbCampaigns.length > 0 && (
                  <>
                    <span className="analysis-limit">{analysisQuota ? `${analysisQuota.remaining} of ${analysisQuota.limit} analyses available before this action` : 'Checking analysis allowance…'}</span>
                    <div className="campaign-actions">
                    <button type="button" className="btn btn-primary" disabled={busy || selectedIds.size === 0} onClick={handleImport}>
                      {busy ? 'Starting AdPilot…' : `${analysisQuota?.remaining === 0 ? 'Watch' : 'Watch & analyze'} selected (${selectedIds.size})`}
                    </button>
                    {/* Importing is unlimited and watches nothing — it brings the
                        whole account catalog in so monitoring becomes a toggle
                        instead of a re-import. */}
                    {owner && unimportedCount > 0 && (
                      <button type="button" className="btn btn-secondary" disabled={busy} onClick={handleImportAll}>
                        Import all {fbCampaigns.length} without watching
                      </button>
                    )}
                    </div>
                  </>
                )}
              </>
            )}

            {watchedList.length > 0 && !showImporter && (
              <div className="synced-list">
                {watchedList.map((row) => (
                  <div key={row.platform_campaign_id} className="synced-row">
                    <strong>{row.name || row.platform_campaign_id}</strong>
                    <span>{row.available === false ? 'Unavailable in Meta' : row.status || 'Status unavailable'}</span>
                    <span>{row.last_sync_at || row.imported_at ? `Updated ${new Date(row.last_sync_at || row.imported_at).toLocaleDateString()}` : 'Synced'}</span>
                    <div className="meta-source-actions">{owner && !user.preview && <button type="button" className="text-button" disabled={busy || row.available === false || row.stale}
                      onClick={() => setEditCampaign(row.platform_campaign_id)}>Edit settings</button>}
                    <WatchedCampaignActions campaign={row} connection={connection} user={user} disabled={busy}
                      onChanged={(updated) => setImported((current) => current.map((item) => String(item.platform_campaign_id) === String(updated.platform_campaign_id) ? updated : item))}
                      onUnwatch={() => handleStopWatching(row.platform_campaign_id)} /></div>
                  </div>
                ))}
              </div>
            )}

            {unwatchedImported.length > 0 && !showImporter && (
              <>
                <div className="section-heading">
                  <div>
                    <h2>Imported, not watched</h2>
                    <span>Synced from Meta and ready to watch when a slot is free. AdPilot does not analyze these.</span>
                  </div>
                </div>
                <div className="synced-list">
                  {unwatchedImported.map((row) => (
                    <div key={row.platform_campaign_id} className="synced-row">
                      <strong>{row.name || row.platform_campaign_id}</strong>
                      <span>{row.available === false ? 'Unavailable in Meta' : row.status || 'Status unavailable'}</span>
                      <span>{row.last_sync_at || row.imported_at ? `Updated ${new Date(row.last_sync_at || row.imported_at).toLocaleDateString()}` : 'Synced'}</span>
                      <div className="meta-source-actions">{owner && !user.preview && <button type="button" className="text-button" disabled={busy || row.available === false || row.stale}
                        onClick={() => setEditCampaign(row.platform_campaign_id)}>Edit settings</button>}
                      <button
                        type="button"
                        className="text-button"
                        disabled={busy || row.available === false || availableCampaignSlots === 0}
                        title={availableCampaignSlots === 0 ? `All ${CAMPAIGN_LIMIT} monitoring slots are in use — stop watching a campaign first` : undefined}
                        onClick={() => handleWatch(row.platform_campaign_id)}
                      >
                        {availableCampaignSlots === 0 ? 'No slots free' : 'Watch'}
                      </button></div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          {!owner && <div className="notice" role="status">You’re collaborating in this workspace. You can manage watched campaigns and recommendations; the owner manages the Meta connection.</div>}

          {/* The brief covers the WATCHED set — analysis never runs on
              imported-but-unwatched campaigns, so an import-only state has no
              brief to open yet. */}
          {watchedList.length > 0 && (
            <button type="button" className="btn btn-primary" onClick={() => navigate(briefPath)}>Open today’s brief →</button>
          )}
        </>
      )}
        </>
      ))}

      {activeChannel === 'google-ads' && <GoogleAdsConnectCard user={user} oauthState={oauthState} />}
      {activeChannel === 'universal-ads' && <UniversalAdsConnectCard user={user} />}
      {activeChannel === 'chatgpt-ads' && <ChatGptAdsConnectCard user={user} />}
      {activeChannel === 'facebook' && active && owner && editCampaign && <MetaCampaignEditor key={`${connection.connection_id}:${connection.ad_account_id}:${editCampaign}`}
        connectionId={connection.connection_id} campaignId={editCampaign} onClose={() => setEditCampaign(null)}
        onChanged={async () => { try { await refreshImported(connection); } catch (err) { setError(campaignErrorMessage(err)); } }} />}
    </div>
  );
}

export default ConnectPage;
